// components
import Toolbar from './components/Toolbar';
import { ZenPortal } from './components/ZenPortal';
import {
  StyledMainContentContainer,
  StyledToolbarSetContainer
} from './ToolbarWrapper.styles';

// enums
import { ToolbarFlowEnum, ToolbarPositionEnum } from './ToolbarWrapper.enums';

// hooks
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useSessionStorage } from 'usehooks-ts';

// types
import { ToolbarWrapperProps, ZenToolbarAnchor } from './ToolbarWrapper.types';

// utils
import { CLOSE_DELAY_MS, ZEN_PORTAL_ANCHOR } from './ToolbarWrapper.consts';

export const ToolbarWrapper = ({
  children,
  actions,
  additionalActions,
  toolbarDataTourId,
  dateNavigation,
  zenPinned,
  planUnitSelector,
  toolbarFlow = ToolbarFlowEnum.GRID,
  component
}: ToolbarWrapperProps) => {
  const [toolbarPosition, setToolbarPosition] =
    useSessionStorage<ToolbarPositionEnum>(
      'toolbar-position',
      ToolbarPositionEnum.TOP
    );
  const [zenOpen, setZenOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = useState<ZenToolbarAnchor>({
    top: ZEN_PORTAL_ANCHOR.top,
    right: ZEN_PORTAL_ANCHOR.right
  });

  const isRightPositioned = toolbarPosition === ToolbarPositionEnum.RIGHT;
  const isZenPositioned = toolbarPosition === ToolbarPositionEnum.ZEN;

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    if (zenPinned) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(
      () => setZenOpen(false),
      CLOSE_DELAY_MS
    );
  };

  const recomputeAnchor = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setAnchor({
      top: Math.max(0, rect.top) + ZEN_PORTAL_ANCHOR.top,
      right:
        Math.max(0, window.innerWidth - rect.right) + ZEN_PORTAL_ANCHOR.right
    });
  }, []);

  useLayoutEffect(() => {
    recomputeAnchor();
    const onScroll = () => requestAnimationFrame(recomputeAnchor);
    const onResize = () => requestAnimationFrame(recomputeAnchor);

    const ro = new ResizeObserver(() => recomputeAnchor());
    if (containerRef.current) ro.observe(containerRef.current);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [recomputeAnchor]);

  useEffect(() => {
    if (!isZenPositioned) return;
    if (zenPinned) {
      clearCloseTimer();
      setZenOpen(true);
    }
    const onKey = (e: KeyboardEvent) => {
      if (isZenPositioned && e.key === 'Escape') setZenOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zenPinned, isZenPositioned]);

  return (
    <StyledToolbarSetContainer
      ref={containerRef}
      isRightPositioned={isRightPositioned}
      data-tourid={toolbarDataTourId}
    >
      {/* Zen mode: render hotzone + toolbar OUTSIDE flow via portal */}
      {isZenPositioned ? (
        <ZenPortal
          open={zenOpen}
          onEnter={() => {
            clearCloseTimer();
            setZenOpen(true);
          }}
          onLeave={scheduleClose}
          anchor={anchor}
        >
          <Toolbar
            actions={actions}
            additionalActions={additionalActions}
            dateNavigation={dateNavigation}
            isRightPositioned={false}
            isZenPositioned
            setToolbarPosition={setToolbarPosition}
            zenOpen={zenOpen}
            onMouseEnterZen={() => {
              clearCloseTimer();
              setZenOpen(true);
            }}
            onMouseLeaveZen={scheduleClose}
            planUnitSelector={planUnitSelector}
            toolbarFlow={toolbarFlow}
            component={component}
          />
        </ZenPortal>
      ) : (
        // Normal/top/right layout inside the flow
        <Toolbar
          actions={actions}
          additionalActions={additionalActions}
          dateNavigation={dateNavigation}
          isRightPositioned={isRightPositioned}
          isZenPositioned={false}
          setToolbarPosition={setToolbarPosition}
          planUnitSelector={planUnitSelector}
          toolbarFlow={toolbarFlow}
          component={component}
        />
      )}

      <StyledMainContentContainer>{children}</StyledMainContentContainer>
    </StyledToolbarSetContainer>
  );
};
